import { useEffect, useState } from "react";
import { C } from "../theme/colors";
import Divider from "../components/Divider";
import Section from "../components/Section";
import PageHero from "../components/PageHero";

const KEY_CONTACT_API_URL = "https://ccapi.rapifuzz.in:8443/api/key-contact/";
const FIND_US_API_URL = "https://ccapi.rapifuzz.in:8443/api/find-us/";
const CONTACT_FORM_API_URL = "https://ccapi.rapifuzz.in:8443/api/contact-form/";

export default function ContactPage() {
  const staticKeyContacts = [
    {role:"Head, CRENS", name:"Prof. Amrita Priyadarshini", email:"crens.head@bits-pilani.ac.in"},
    {role:"Programme Coordinator", name:"Dr. Rajesh Kumar", email:"crens.programmes@bits-pilani.ac.in"},
    {role:"Research Coordinator", name:"Dr. Priya Rajan", email:"crens.research@bits-pilani.ac.in"},
    {role:"Industry & MoU Affairs", name:"Shri Vikram Nair", email:"crens.industry@bits-pilani.ac.in"},
    {role:"Media & Communications", name:"Ms. Kavya Sharma", email:"crens.media@bits-pilani.ac.in"},
  ];

  const staticFindUs = [
    {label:"Registered Office",icon:"🏢",lines:["E 203, BITS Pilani, Hyderabad Campus","Jawahar Nagar, Kapra","Hyderabad — 500078"]},
    {label:"Correspondence Address",icon:"✉",lines:["Vice Chancellor Office","BITS Pilani, Pilani","Rajasthan, India — 333031"]},
    {label:"Email",icon:"📧",lines:["crens.head@bits-pilani.ac.in"]},
  ];

  const [keyContacts, setKeyContacts] = useState(staticKeyContacts);
  const [findUs, setFindUs] = useState(staticFindUs);

  useEffect(() => {
    let cancelled = false;

    fetch(KEY_CONTACT_API_URL)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(json => {
        if (cancelled) return;
        const list = Array.isArray(json) ? json : (json?.data || []);
        if (!list.length) return;
        const mapped = list.map(c => ({
          role: c.role || c.designation || c.title || "",
          name: c.name || c.full_name || "",
          email: c.email || c.email_id || "",
        }));
        if (mapped.some(c => c.name)) setKeyContacts(mapped);
      })
      .catch(() => {
        // Silently keep static fallback on any failure
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetch(FIND_US_API_URL)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(json => {
        if (cancelled) return;
        const list = Array.isArray(json) ? json : (json?.data || []);
        if (!list.length) return;
        const mapped = list.map(a => ({
          label: a.label || a.title || a.address_type || "",
          icon: a.icon || "📍",
          lines: Array.isArray(a.lines)
            ? a.lines
            : (a.address || a.description || "").split("\n").filter(Boolean),
        }));
        if (mapped.some(a => a.lines.length)) setFindUs(mapped);
      })
      .catch(() => {
        // Silently keep static fallback on any failure
      });

    return () => { cancelled = true; };
  }, []);

  // ── Send a Message form ──
  const [form, setForm] = useState({ name:"", email:"", phone:"", subject:"", message:"" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [sent, setSent] = useState(false);

  const handleInput = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setFieldErrors(fe => (fe[k] ? { ...fe, [k]: null } : fe));
  };

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Full name is required.";
    if (!form.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }
    if (!form.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else if (!/^\d{10}$/.test(form.phone.trim())) {
      errors.phone = "Please enter a valid 10-digit phone number.";
    }
    if (!form.message.trim()) errors.message = "Message is required.";
    return errors;
  };

  const handleSend = () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitError(null);
    setSubmitting(true);

    fetch(CONTACT_FORM_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        full_name: form.name,
        name: form.name,
        email: form.email,
        phone: form.phone,
        phone_number: form.phone,
        mobile: form.phone,
        subject: form.subject,
        message: form.message,
        description: form.message,
      }),
    })
      .then(res =>
        res.json()
          .catch(() => ({}))
          .then(data => ({ ok: res.ok, status: res.status, data }))
      )
      .then(({ ok, status, data }) => {
        if (!ok || data?.status === false) {
          throw new Error(data?.message || `Request failed (${status})`);
        }
        setSent(true);
        setForm({ name:"", email:"", phone:"", subject:"", message:"" });
        setTimeout(() => { window.location.reload(); }, 1500);
      })
      .catch(err => {
        setSubmitError(err.message || "Something went wrong sending your message. Please try again, or email us directly.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="page-fade">
      <PageHero title="Contact Us" tag="Get in Touch" subtitle="Reach out to CRENS for research collaborations, partnership inquiries, or programme information."/>

      <Section>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1.4fr",gap:72,alignItems:"start"}} className="grid-2">
          <div>
            <span className="section-tag">Key Contacts</span>
            <h2 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:600,color:C.navyDark,fontSize:"clamp(1.4rem,2.2vw,2rem)",marginTop:10,marginBottom:20}}>Key Contacts</h2>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {keyContacts.map((c,i) => (
                <div key={i} className="card" style={{padding:"16px 20px"}}>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:10.5,fontWeight:700,color:C.textMuted,letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>{c.role}</div>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:15,fontWeight:700,color:C.navyDark,marginBottom:6}}>{c.name}</div>
                  <a href={`mailto:${c.email}`} style={{display:"flex",alignItems:"center",gap:6,fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:C.blue,textDecoration:"none"}}>
                    <span>✉</span>{c.email}
                  </a>
                </div>
              ))}
            </div>
          </div>
          <div>
            <span className="section-tag">Send a Message</span>
            <h2 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:600,color:C.navyDark,fontSize:"clamp(1.4rem,2.2vw,2rem)",marginTop:10}}>Write to Us</h2>
            <Divider/>
            {sent ? (
              <div style={{padding:48,background:C.accent,borderRadius:12,border:`1px solid ${C.accentDeep}`,textAlign:"center"}}>
                <div style={{width:56,height:56,borderRadius:"50%",background:C.navy,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:22,color:"white"}}>✓</div>
                <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:20,fontWeight:600,color:C.navyDark,marginBottom:8}}>Message Sent!</div>
                <p className="body">Thank you for reaching out. We'll get back to you shortly.</p>
              </div>
            ) : (
              <div className="card" style={{padding:32}}>
                <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                    <div>
                      <label style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,color:C.navyDark,display:"block",marginBottom:6}}>Full Name <span style={{color:"#C0392B"}}>*</span></label>
                      <input type="text" placeholder="Your full name" value={form.name} onChange={e=>handleInput("name",e.target.value)}
                        style={{borderColor: fieldErrors.name ? "#C0392B" : undefined}}/>
                      {fieldErrors.name && <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:"#C0392B",marginTop:5}}>{fieldErrors.name}</div>}
                    </div>
                    <div>
                      <label style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,color:C.navyDark,display:"block",marginBottom:6}}>Email <span style={{color:"#C0392B"}}>*</span></label>
                      <input type="email" placeholder="your@email.com" value={form.email} onChange={e=>handleInput("email",e.target.value)}
                        style={{borderColor: fieldErrors.email ? "#C0392B" : undefined}}/>
                      {fieldErrors.email && <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:"#C0392B",marginTop:5}}>{fieldErrors.email}</div>}
                    </div>
                  </div>
                  <div>
                    <label style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,color:C.navyDark,display:"block",marginBottom:6}}>Phone Number <span style={{color:"#C0392B"}}>*</span></label>
                    <input type="tel" placeholder="Your phone number" value={form.phone} onChange={e=>handleInput("phone",e.target.value)}
                      style={{borderColor: fieldErrors.phone ? "#C0392B" : undefined}}/>
                    {fieldErrors.phone && <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:"#C0392B",marginTop:5}}>{fieldErrors.phone}</div>}
                  </div>
                  <div>
                    <label style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,color:C.navyDark,display:"block",marginBottom:6}}>Subject</label>
                    <input type="text" placeholder="How can we help?" value={form.subject} onChange={e=>handleInput("subject",e.target.value)}/>
                  </div>
                  <div>
                    <label style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,color:C.navyDark,display:"block",marginBottom:6}}>Message <span style={{color:"#C0392B"}}>*</span></label>
                    <textarea rows={5} placeholder="Your message..." value={form.message} onChange={e=>handleInput("message",e.target.value)}
                      style={{borderColor: fieldErrors.message ? "#C0392B" : undefined}}/>
                    {fieldErrors.message && <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:"#C0392B",marginTop:5}}>{fieldErrors.message}</div>}
                  </div>
                  <button className="btn-primary" onClick={handleSend} disabled={submitting} style={{width:"100%",justifyContent:"center",marginTop:4,opacity:submitting?0.7:1,cursor:submitting?"default":"pointer"}}>
                    {submitting ? "Sending…" : "Send Message →"}
                  </button>
                  {submitError && (
                    <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,color:"#C0392B",textAlign:"center",margin:0}}>{submitError}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section alt>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:60,alignItems:"start"}} className="grid-2">
          <div>
            <span className="section-tag">Our Address</span>
            <h2 style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:600,color:C.navyDark,fontSize:"clamp(1.4rem,2.2vw,2rem)",marginTop:10}}>Find Us</h2>
            <Divider/>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {findUs.map((c,i) => (
                <div key={i} className="card" style={{padding:"18px 20px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <span style={{fontSize:16}}>{c.icon}</span>
                    <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,fontWeight:700,color:C.blue,letterSpacing:1.5,textTransform:"uppercase"}}>{c.label}</span>
                  </div>
                  {c.lines.map((l,j) => <div key={j} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,color:C.textMid,lineHeight:1.9}}>{l}</div>)}
                </div>
              ))}
            </div>
          </div>
          <div>
            <img
              src="uploads/contact.png"
              alt="BITS Pilani Campus"
              style={{width:"100%",height:"100%",minHeight:400,objectFit:"cover",borderRadius:14}}
            />
          </div>
        </div>
      </Section>
    </div>
  );
}